import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { GraduationCap, Camera, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, resolveImg, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const L = {
  eyebrow: { en: "Students Club", cs: "Studentský klub", he: "מועדון הסטודנטים", ru: "Студенческий клуб" },
  title: { en: "Get your student discount card", cs: "Získejte studentskou slevovou kartu", he: "קבלו את כרטיס ההנחה לסטודנטים", ru: "Получите студенческую скидочную карту" },
  intro: { en: "Fill in a few details and upload a photo. Once approved, your digital card arrives by email and works right from your phone.", cs: "Vyplňte pár údajů a nahrajte fotku. Po schválení vám digitální karta přijde e-mailem a funguje přímo z telefonu.", he: "מלאו כמה פרטים והעלו תמונה. לאחר האישור הכרטיס הדיגיטלי יישלח למייל ויעבוד ישירות מהטלפון.", ru: "Заполните несколько полей и загрузите фото. После одобрения цифровая карта придёт на e-mail и будет работать прямо с телефона." },
  name: { en: "Full name", cs: "Celé jméno", he: "שם מלא", ru: "Полное имя" },
  phone: { en: "Phone (WhatsApp)", cs: "Telefon (WhatsApp)", he: "טלפון (וואטסאפ)", ru: "Телефон (WhatsApp)" },
  email: { en: "Email (for your card)", cs: "E-mail (pro kartu)", he: "אימייל (לקבלת הכרטיס)", ru: "E-mail (для карты)" },
  university: { en: "University", cs: "Univerzita", he: "אוניברסיטה", ru: "Университет" },
  faculty: { en: "Faculty", cs: "Fakulta", he: "פקולטה", ru: "Факультет" },
  year: { en: "Year of study", cs: "Ročník", he: "שנת לימודים", ru: "Курс" },
  photo: { en: "Your photo (shown on the card)", cs: "Vaše fotka (zobrazí se na kartě)", he: "תמונה שלכם (תוצג על הכרטיס)", ru: "Ваше фото (будет на карте)" },
  choose: { en: "Choose photo", cs: "Vybrat fotku", he: "בחירת תמונה", ru: "Выбрать фото" },
  confirm: { en: "I confirm I am a current student", cs: "Potvrzuji, že jsem student/ka", he: "אני מאשר/ת שאני סטודנט/ית פעיל/ה", ru: "Подтверждаю, что я студент" },
  submit: { en: "Request my card", cs: "Požádat o kartu", he: "שליחת בקשה", ru: "Запросить карту" },
  required: { en: "Required", cs: "Povinné", he: "שדה חובה", ru: "Обязательно" },
  doneTitle: { en: "Thank you — request received!", cs: "Děkujeme — žádost přijata!", he: "תודה — הבקשה התקבלה!", ru: "Спасибо — запрос получен!" },
  doneDesc: { en: "We'll review it shortly. Your digital card will be sent to you once approved.", cs: "Brzy ji zkontrolujeme. Po schválení vám pošleme digitální kartu.", he: "נבדוק אותה בקרוב. הכרטיס הדיגיטלי יישלח לאחר האישור.", ru: "Мы скоро её проверим. Цифровая карта будет отправлена после одобрения." },
  closed: { en: "Student card sign-up is currently closed.", cs: "Registrace karet je momentálně uzavřena.", he: "ההרשמה לכרטיסים סגורה כעת.", ru: "Регистрация карт сейчас закрыта." },
};

export default function StudentCardSignup() {
  const { lang } = useLang();
  const tx = (k) => L[k][lang] || L[k].en;
  const fileRef = useRef(null);
  const [form, setForm] = useState({ name: "", phone: "", email: "", university: "", faculty: "", year: "", photo_url: "", confirm: false });
  const [errors, setErrors] = useState({});
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const { data: settings } = useQuery({ queryKey: ["student-card-settings"], queryFn: async () => (await api.get("/student-card-settings")).data });

  const set = (k) => (e) => { setForm({ ...form, [k]: e.target.value }); setErrors((x) => ({ ...x, [k]: false })); };
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/public-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, photo_url: data.url })); setErrors((x) => ({ ...x, photo_url: false }));
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed"); } finally { setUploading(false); }
  };
  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    ["name", "phone", "university", "faculty", "year", "photo_url"].forEach((k) => { if (!String(form[k] || "").trim()) errs[k] = true; });
    if (!form.confirm) errs.confirm = true;
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSubmitting(true);
    try {
      const { confirm, ...payload } = form;
      await api.post("/student-cards", { ...payload, lang });
      setDone(true);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong"); } finally { setSubmitting(false); }
  };

  const field = (k, type = "text") => (
    <div key={k}>
      <Label htmlFor={`sc-${k}`} className={errors[k] ? "text-red-500" : ""}>{tx(k)}{k !== "email" && <span className="text-[#BE923C]"> *</span>}</Label>
      <Input id={`sc-${k}`} data-testid={`student-card-${k}`} type={type} value={form[k]} onChange={set(k)} className={`mt-1.5 ${errors[k] ? "border-red-400" : "border-[#0F172A]/10"}`} />
      {errors[k] && <p className="mt-1 text-xs text-red-500">{tx("required")}</p>}
    </div>
  );

  return (
    <div className="relative bg-[#F9F8F6] min-h-screen overflow-x-hidden" data-testid="student-card-signup-page">
      <Navbar />
      <section className="pt-28 sm:pt-32 pb-24">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-[1fr_1.1fr] gap-12 items-start">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#BE923C] flex items-center gap-2"><GraduationCap size={16} /> {tx("eyebrow")}</p>
            <h1 className="mt-4 font-serif text-4xl sm:text-5xl tracking-tight text-[#0F172A] leading-[1.02]">{tx("title")}</h1>
            <p className="mt-5 text-[#475569] text-base sm:text-lg leading-relaxed max-w-lg">{tx("intro")}</p>
            {settings && <div className="mt-8 inline-flex items-center gap-3 rounded-2xl bg-[#0F172A] text-white px-5 py-4" data-testid="student-card-discount-preview"><span className="font-serif text-xl text-[#BE923C]">%</span><span className="text-sm font-semibold">{settings.discount_text?.[lang] || settings.discount_text?.en}</span></div>}
          </div>
          {done ? (
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="student-card-success" className="rounded-3xl bg-white border border-[#8C9B86]/50 p-10 text-center">
              <span className="mx-auto w-14 h-14 rounded-full bg-[#8C9B86]/15 flex items-center justify-center text-[#6f8069]"><CheckCircle2 size={28} /></span>
              <h3 className="mt-4 font-serif text-2xl">{tx("doneTitle")}</h3>
              <p className="mt-2 text-[#475569]">{tx("doneDesc")}</p>
            </motion.div>
          ) : settings && !settings.active ? (
            <div className="rounded-3xl bg-white border border-[#0F172A]/10 p-10 text-center text-[#475569]" data-testid="student-card-closed">{tx("closed")}</div>
          ) : (
            <form onSubmit={submit} className="rounded-3xl bg-white border border-[#0F172A]/5 p-6 sm:p-8 space-y-5 shadow-[0_20px_60px_rgba(15,23,42,0.06)]" data-testid="student-card-form">
              {field("name")}
              <div className="grid sm:grid-cols-2 gap-4">{field("phone", "tel")}{field("email", "email")}</div>
              {field("university")}
              <div className="grid sm:grid-cols-2 gap-4">{field("faculty")}{field("year")}</div>
              <div>
                <Label className={errors.photo_url ? "text-red-500" : ""}>{tx("photo")}<span className="text-[#BE923C]"> *</span></Label>
                <div className="mt-2 flex items-center gap-4">
                  <div className={`w-24 h-24 rounded-2xl overflow-hidden border-2 flex items-center justify-center bg-[#F4F2EE] ${errors.photo_url ? "border-red-400" : "border-[#BE923C]/40"}`}>
                    {form.photo_url ? <img src={resolveImg(form.photo_url)} alt="" className="w-full h-full object-cover" data-testid="student-card-photo-preview" /> : <Camera size={26} className="text-[#94a3b8]" />}
                  </div>
                  <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="student-card-photo-btn"
                    className="inline-flex items-center gap-2 rounded-full border border-[#0F172A]/15 px-5 py-2.5 text-sm font-semibold hover:border-[#BE923C] transition-colors disabled:opacity-60">
                    {uploading ? <Loader2 size={15} className="animate-spin" /> : <Camera size={15} />} {tx("choose")}
                  </button>
                  <input ref={fileRef} type="file" accept="image/*" capture="user" className="hidden" data-testid="student-card-photo-input" onChange={upload} />
                </div>
              </div>
              <label className={`flex items-start gap-3 text-sm cursor-pointer ${errors.confirm ? "text-red-500" : "text-[#0F172A]"}`}>
                <input type="checkbox" data-testid="student-card-confirm" checked={form.confirm} onChange={(e) => { setForm({ ...form, confirm: e.target.checked }); setErrors((x) => ({ ...x, confirm: false })); }} className="mt-0.5 w-4 h-4 accent-[#BE923C]" />
                {tx("confirm")}
              </label>
              <button type="submit" disabled={submitting || uploading} data-testid="student-card-submit"
                className="w-full rounded-full bg-[#BE923C] hover:bg-[#a87f33] text-white font-semibold py-3.5 transition-colors disabled:opacity-60">
                {submitting ? <Loader2 size={18} className="animate-spin mx-auto" /> : tx("submit")}
              </button>
            </form>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
